import React from 'react';

/**
 * ErrorBoundary — Filet Anti-Crash global (Fix 2026-09-21)
 *
 * Empêche qu'une erreur JS locale fasse disparaître tout l'arbre React
 * et produise un écran noir intégral (cas historique : crash `null.lat`
 * sur `driver.location.lat` du 20/09/2026).
 *
 * Comportement :
 *  - Capture toute erreur non gérée du sous-arbre React (componentDidCatch)
 *  - Affiche une page de secours claire et utilisable (jamais silencieuse)
 *  - Erreur intégrale conservée dans console.error pour debug
 *  - Bouton "Recharger la page" → window.location.reload()
 *  - Lien mailto contact@metro-taxi.com pré-rempli avec l'erreur
 *  - Détails techniques repliés dans un <details> pour signalement précis
 *
 * Périmètre : rien d'autre. Aucun système métier touché.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // NE JAMAIS masquer silencieusement — log complet en console
    console.error('[ErrorBoundary] Uncaught React error:', error);
    console.error('[ErrorBoundary] Component stack:', errorInfo?.componentStack);
    this.setState({ errorInfo });
  }

  handleReload = () => {
    window.location.reload();
  };

  buildMailtoLink = () => {
    const { error, errorInfo } = this.state;
    const subject = encodeURIComponent('Signalement erreur Métro-Taxi');
    const bodyLines = [
      'Bonjour,',
      '',
      'Une erreur est survenue dans l\'application Métro-Taxi.',
      '',
      `URL : ${window.location.href}`,
      `Date : ${new Date().toISOString()}`,
      `User-Agent : ${navigator.userAgent}`,
      '',
      '--- Erreur ---',
      error?.message || 'inconnue',
      '',
      '--- Component stack ---',
      errorInfo?.componentStack || 'non disponible',
    ];
    const body = encodeURIComponent(bodyLines.join('\n'));
    return `mailto:contact@metro-taxi.com?subject=${subject}&body=${body}`;
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const { error, errorInfo } = this.state;

    return (
      <div
        className="min-h-screen w-full bg-[#09090B] flex items-center justify-center p-6"
        data-testid="error-boundary-fallback"
      >
        <div className="max-w-md w-full">
          {/* Icône alerte */}
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 rounded-full bg-[#FFD60A]/15 border border-[#FFD60A]/40 flex items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#FFD60A"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </div>
          </div>

          {/* Titre */}
          <h1
            className="text-2xl font-bold text-[#FFD60A] text-center mb-3"
            data-testid="error-boundary-title"
          >
            Une erreur est survenue
          </h1>

          {/* Message explicatif */}
          <p className="text-zinc-300 text-sm text-center leading-relaxed mb-6">
            Un problème inattendu a interrompu l'application. Rassure-toi, tes données sont en sécurité.
            Recharge la page pour reprendre là où tu étais.
          </p>

          {/* Bouton Recharger */}
          <button
            onClick={this.handleReload}
            className="w-full h-11 bg-[#FFD60A] hover:bg-[#FFE55C] text-black font-bold rounded-lg transition-colors mb-4"
            data-testid="error-boundary-reload-btn"
          >
            Recharger la page
          </button>

          {/* Lien support */}
          <p className="text-zinc-500 text-xs text-center mb-4">
            Le problème persiste ?{' '}
            <a
              href={this.buildMailtoLink()}
              className="text-[#FFD60A] hover:underline"
              data-testid="error-boundary-support-link"
            >
              contact@metro-taxi.com
            </a>
          </p>

          {/* Détails techniques repliés */}
          <details className="mt-6 text-zinc-500 text-xs" data-testid="error-boundary-details">
            <summary className="cursor-pointer hover:text-zinc-300 select-none">
              Détails techniques
            </summary>
            <div className="mt-3 p-3 bg-zinc-900/60 border border-zinc-800 rounded-lg font-mono text-[11px] leading-relaxed max-h-64 overflow-auto">
              <div className="text-red-400 mb-2 break-all">
                {error?.name || 'Error'}: {error?.message || 'unknown error'}
              </div>
              {errorInfo?.componentStack && (
                <pre className="whitespace-pre-wrap text-zinc-500">
                  {errorInfo.componentStack}
                </pre>
              )}
            </div>
          </details>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
